import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { buildCanonical } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { LEGAL_DOC_SLUGS, LEGAL_PATHS } from "../../legal/legalPaths";
import { getLegalDocument, LEGAL_LAST_UPDATED } from "../../legal/legalRegistry";
import { getLegalSections } from "../../legal/legalDocumentSections";
import "./legal-pages.css";

export default function LegalDocumentPage() {
  const { slug } = useParams();
  const doc = useMemo(() => getLegalDocument(slug), [slug]);
  const sections = useMemo(() => (doc ? getLegalSections(doc.slug) : []), [doc]);

  usePageSeo(
    doc
      ? {
          title: `${doc.title} | Treydora Legal`,
          description: doc.shortDescription || doc.summary,
          canonicalUrl: buildCanonical(doc.path),
          ogUrl: buildCanonical(doc.path),
        }
      : null
  );

  if (!slug || !LEGAL_DOC_SLUGS.includes(String(slug).toLowerCase()) || !doc) {
    return <Navigate to={LEGAL_PATHS.center} replace />;
  }

  return (
    <div className="td-legal td-legal--document">
      <header className="td-legal-doc-hero">
        <div className="td-legal-doc-hero__aurora" aria-hidden />
        <div className="td-legal-doc-hero__inner">
          <p className="td-legal-doc-hero__crumb">
            <Link to={LEGAL_PATHS.center}>Legal Center</Link>
            <span aria-hidden> / </span>
            <span>{doc.title}</span>
          </p>
          <span className={`td-legal-doc-hero__badge td-legal-doc-hero__badge--${doc.categoryTone}`}>{doc.category}</span>
          <h1 className="td-legal-doc-hero__title">{doc.title}</h1>
          <p className="td-legal-doc-hero__summary">{doc.summary}</p>
          <p className="td-legal-doc-hero__updated">Last updated · {LEGAL_LAST_UPDATED}</p>
        </div>
      </header>

      <div className="td-legal-doc-layout">
        <aside className="td-legal-toc td-legal-toc--desktop" aria-label="On this page">
          <p className="td-legal-toc__label">Seal index</p>
          <nav>
            <ol className="td-legal-toc__list">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <div className="td-legal-doc-main">
          <details className="td-legal-toc-mobile">
            <summary>On this page · table of contents</summary>
            <nav aria-label="On this page mobile">
              <ol className="td-legal-toc__list">
                {sections.map((s) => (
                  <li key={`m-${s.id}`}>
                    <a href={`#${s.id}`}>{s.title}</a>
                  </li>
                ))}
              </ol>
            </nav>
          </details>

          {/* DEVELOPER: Starter legal copy — attorney review required before launch. */}
          <p className="td-legal-devcallout td-legal-devcallout--inline" role="note">
            <strong>Internal note.</strong> This page contains starter template policies for UX wiring only. It is not
            finalized legal advice and must be reviewed by counsel.
          </p>

          {doc.showWarning ? (
            <div className="td-legal-callout" role="region" aria-label="Important notice">
              <div className="td-legal-callout__icon" aria-hidden>
                <i className="fas fa-shield-alt" />
              </div>
              <div>
                <h2 className="td-legal-callout__title">{doc.warningTitle}</h2>
                <p className="td-legal-callout__body">{doc.warningBody}</p>
              </div>
            </div>
          ) : null}

          <article className="td-legal-body">
            {sections.map((sec) => (
              <section key={sec.id} id={sec.id} className="td-legal-body__section">
                <h2 className="td-legal-body__h2">{sec.title}</h2>
                {sec.paragraphs.map((p, i) => (
                  <p key={i} className="td-legal-body__p">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </article>

          <p className="td-legal-doc-back">
            <Link to={LEGAL_PATHS.center} className="td-legal-inline-link">
              ← Return to Legal Center
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
